<template>
  <layout>
    <template v-slot:header>
      <AppHead :title="`Product Variations - ${product.sku}`" />
      <h2>Variations - Product {{ product.sku }} - {{ product.name }}</h2>
      <flash-messages />
    </template>
        
    <template v-slot:default>

      <card title="Add Attributes" class="mb-3">
        <div class="table-responsive">
          <table class="table table-striped tbody-border" cellpadding="0" cellspacing="0">
            <thead>
              <tr>
                <th class="px-2">Name</th>
                <th class="px-2">Options</th>
                <th class="px-2"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="attribute in product.variation_attributes" :key="attribute.id">
                <td class="pl-2" width="48.5%">
                  <p class="font-weight-bold mb-0">{{ attribute.name }}</p>
                </td>
                <td>
                  <table cellpadding="0" cellspacing="0">
                    <draggable v-if="attribute.values.length" class="list-unstyled mb-2" :list="attribute.values" item-key="id" :delay="30" @change="onOrder(attribute)">
                      <template #item="{element}">
                        <li>
                          <p class="my-1 badge badge-secondary m-w-100 py-1 px-2 py-1 w-100 cursor-pointer" :title="`Drag to reorder ${element.value}`">{{element.value}}</p>
                        </li>
                      </template>
                    </draggable>

                    <tr class="bg-transparent">
                      <td class="p-0 border-0">
                        <div class="mb-2 mt-1 d-flex align-items-center">
                          <input type="text" class="form-control" v-model="form.attribute_value"/>
                          <i @click="addAttributeValue(attribute)" class="fas fa-plus icon-hover text-secondary fa-lg ml-2"></i>
                        </div>
                      </td>
                    </tr>
                  </table>
                </td>
                <td class="flex-center mt-2 mt-lg-2 border-0">
                  <i @click="removeAttribute(attribute)" class="fas fa-trash icon-hover text-secondary fa-lg"></i>
                </td>
              </tr>
              <tr>
                <td colspan="2">
                  <div class="py-3 px-2 d-flex flex-column flex-lg-row">
                    <input type="text" class="form-control mr-3 mb-2 mb-lg-0" v-model="form.attribute_name" />

                    <textarea class="form-control" rows="5" placeholder="Optional, options one per line" v-model="form.attribute_options"></textarea>
                  </div>
                </td>
                <td>
                  <i @click="addAttribute()" class="fas fa-plus icon-hover text-secondary fa-lg flex-center mt-3"></i>
                </td>
            </tr>
            </tbody>
          </table>
        </div>
      </card>

      <card title="Included Products">
        <div class="table-responsive">
          <table class="table table-striped tbody-border" cellpadding="0" cellspacing="0">
            <thead>
              <tr>
                <th></th>
                <th>SKU</th>
                <th>Name</th>
                <th>Attribute Values</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="included in included_products" :key="included.id">
              <tr v-if="(!form.sku_match || !form.sku_match.length) || included.sku.includes(form.sku_match)">
                <td>
                  <div class="form-check pl-0 pl-2 pt-2">
                    <label class="mb-0">Default:</label> 
                    <input type="checkbox" class="ml-1" v-model="included.is_default" @change="setDefault(included)"/>
                  </div>

                  <FileUpload 
                    mode="advanced" 
                    :auto="true" 
                    :multiple="true"
                    accept="image/*" 
                    name="images[]" 
                    :customUpload="true" 
                    :showUploadButton="false"
                    :showCancelButton="false"
                    @uploader="(event) => addImagesIncluded(event, included)" >
                    <template #empty>
                      <div class="list-file-select px-3 py-4">
                        <p class="mb-0">Drag image here.</p>
                      </div>
                    </template>
                  </FileUpload>

                  <img v-for="image in included.images" class="product-image cursor-pointer p-2" :src="image.thumb_url" />
                </td>
                <td>
                  <p class="badge badge-primary px-2">{{ included.sku }}</p>
                </td>
                <td>{{ included.name }}</td>
                <td>
                <table cellpadding="0" cellspacing="0">
                  <tr v-for="attribute in product.variation_attributes" :key="attribute.id" class="bg-transparent">
                    <td>
                      <label for="attribute-select" class="mb-1">{{ attribute.name }}</label>
                      <select id="attribute-select" class="form-control w-100" @change="(e) => variationProductSetAttributeValue(e, included)">
                        <option :value="getCurrentValue(included, attribute).id" selected>{{ getCurrentValue(included, attribute).value }}</option>
                        <option v-for="value in attribute.values" :value="value.id">{{ value.value }}</option>
                      </select>
                    </td>
                  </tr>
                </table>
                </td>
                <td class="flex-center">
                  <i @click="removeProduct(included)" class="fas fa-trash icon-hover text-secondary fa-lg mt-3"></i>
                </td>
              </tr>
              </template>
            </tbody>
          </table>

          <div class="col-sm-5">
            <p class="mb-0">Add Images named by SKU / part of SKU</p>
            <input type="text" class="form-control" v-model="form.sku_match"/>
            <FileUpload 
              mode="advanced" 
              :auto="true"
              class="mt-2"
              :multiple="true"
              accept="image/*" 
              name="images[]" 
              :customUpload="true" 
              :showUploadButton="false"
              :showCancelButton="false"
              @uploader="(event) => addImagesVariation(event)" >
              <template #empty>
                <div class="file-select">
                  <p class="mb-0">Drag image here.</p>
                </div>
              </template>
            </FileUpload>
          </div>
        </div>
      </card>


      <card title="Add Product" icon="fas fa-plus" class="mt-3">
        <Search :searchText="searchText" @searched="search"/>

        <jet-button class="mr-2" @click="includeAllProducts()">Add All Results</jet-button>

        <DataTable ref="admin-products-lookup"
        :value="products" :paginator="true"
        :lazy="true"
        :first="(pagination.page - 1) * pagination.per_page"
        :rows="pagination.per_page"
        :totalRecords="pagination.total"
        @page="onPage($event)"
        :sortField="sortField"
        :sortOrder="sortOrder"
        @sort="onSort"
        @state-restore="onTableStateRestore($event)"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown" currentPageReportTemplate="({first} - {last} of {totalRecords})"
        paginatorPosition="both" removableSort
        :rowsPerPageOptions="[10, 20, 50, 100]"
        class="p-datatable-sm admin-datatable"
        tableClass="border-left border-right border-bottom"
        stateStorage="local" stateKey="admin-products-lookup" dataKey="id"
        @row-click="(e) => includeProduct(e.data.id)">
            <template #paginatorLeft>
              {{ products.length }} Products
            </template>
            <Column field="name" header="Name" :sortable="true"></Column>
            <Column field="sku" header="SKU" :sortable="true"></Column>
            <Column field="price_msrp" header="MSRP" :sortable="true">
              <template #body="slotProps">
                {{ formatDollars(slotProps.data.price_msrp) }}
              </template>
            </Column>

            <Column field="inventoryInfo" header="Inventory" :sortable="true" sortField="inventoryInfo.current_inventory">
              <template #body="slotProps">
                <InventoryInfo :inventoryInfo="slotProps.data.inventoryInfo" />
              </template>
            </Column>

            <Column field="created_at" header="Created" :sortable="true">
              <template #body="slotProps">
                {{ moment(slotProps.data.created_at).format('M/D/YY h:mm A') }}
              </template>
            </Column>
            <Column field="updated_at" header="Updated" :sortable="true">
              <template #body="slotProps">
                {{ moment(slotProps.data.updated_at).format('M/D/YY h:mm A') }}
              </template>
            </Column>
        </DataTable>
      </card>

    </template>
  </layout>
</template>

<script>

import Layout from '@/Shared/Admin/Layout'
import JetButton from '@/Jetstream/Button'
import JetFormSection from '@/Jetstream/FormSection'
import JetInput from '@/Jetstream/Input'
import JetCheckbox from '@/Jetstream/Checkbox'
import JetInputError from '@/Jetstream/InputError'
import JetLabel from '@/Jetstream/Label'
import JetActionMessage from '@/Jetstream/ActionMessage'
import AmountInput from '@/Shared/AmountInput'
import Search from '@/Shared/Admin/Search'
import FileUpload from 'primevue/fileupload';
import toastr from 'toastr'
import moment from 'moment'
import draggable from 'vuedraggable'
import InventoryInfo from '@/Components/General/InventoryInfo'

export default {
  components: {
    JetActionMessage,
    JetButton,
    JetFormSection,
    JetInput,
    JetCheckbox,
    JetInputError,
    JetLabel,
    Layout,
    Search,
    AmountInput,
    FileUpload,
    draggable,
    InventoryInfo,
  },
  props: {
    product: Object,
    included_products: Array,
    total_price_msrp: Number,
    total_wholesale: Number,
  },
  remember: 'form',
  data() {
    return {
      sortField: null, // current sort field
      sortOrder: null, // current sort order
      pagination: {
        total: 0,
        per_page: 20,
        page: 1,
      },
      searchText: null,
      products: [],
      form: this.$inertia.form({
        included_product_id: null,
        included_product_ids: null,
        attribute_name: null,
        attribute_options: null,
        attribute_value: null,
        description: null,
        sku_match: null,
      }),
    }
  },
  created() {
    this.moment = moment;
  },
  methods: {
    search(e, searchText) {
      this.searchText = searchText
      this.$refs['admin-products-lookup'].resetPage()

      this.postSearch()
    },
    orderVariationAttributes() {
      this.product.variation_attributes.forEach(attributes => {
        attributes.values.forEach( (value, index) => {
          value.sort_order = index
        })
      })
    },
    onOrder(attribute) {
      this.orderVariationAttributes()
      let postedValues = []
      attribute.values.forEach(val => {
        postedValues.push({
          id: val.id,
          value: val.value,
          sort_order: val.sort_order
        })
      })

      this.$inertia.post(route('admin.products.variation.savevaluesortorder', attribute.id), {
          values: JSON.stringify(postedValues)
        })
    },
    onTableStateRestore(e) {
      this.pagination.page = e.first / e.rows + 1
      this.pagination.per_page = e.rows
      this.sortField = e.sortField
      this.sortOrder = e.sortOrder
    },
    onPage(e) {
      this.pagination.page = e.page + 1
      this.pagination.per_page = e.rows
      this.postSearch()
    },
    onSort(events) {
      this.sortField = events.sortField;
      this.sortOrder = events.sortOrder;
      this.postSearch();
    },
    postSearch() {
      axios.post(route('products.search'), {
        sort_field: this.sortField,
        sort_order: this.sortOrder,
        query: this.searchText || '',
        include_inventory: 1,
        filter_active: -1,
        manufacturer_id: this.product.manufacturer_id
      }, {
        params: {
          page: this.pagination.page, per_page: this.pagination.per_page
        }
      }).then((response) => {
        if (response.data.success) {
          this.products = response.data.data;
          this.pagination.total = response.data.total
        }
      })
    },
    includeProduct(id) {
      this.form.included_product_id = id;
      this.form.post(route('admin.products.variationAdd', this.product), {preserveScroll: true});
    },
    includeAllProducts() {
      this.form.included_product_ids = this.products.map((product) => product.id);
      this.form.post(route('admin.products.variationAddProducts', this.product), {preserveScroll: true});
    },
    removeProduct(product) {
      this.form.included_product_id = product.id;
      this.form.post(route('admin.products.variationRemove', this.product), {preserveScroll: true});
    },
    setDefault(product) {
      this.form.included_product_id = product.id;
      this.form.post(route('admin.products.setdefault', this.product), {preserveScroll: true});
    },
    addAttribute() {
      this.form.post(route('admin.products.attributeadd', this.product), {preserveScroll: true});
    },
    addAttributeValue(attribute) {
      this.form.post(route('admin.products.attributeaddvalue', attribute), {preserveScroll: true});
    },
    removeAttribute(attribute) {
      this.form.post(route('admin.products.attributeremove', attribute), {preserveScroll: true});
    },
    variationProductSetAttributeValue(e, included) {
      this.form.post(route('admin.products.variationproductsetattributevalue', {product: included, value: e.target.value}), {preserveScroll: true});
    },
    getCurrentValue(included, attribute) {
      for (var i=0; i<included.values.length; i++) {
        if (included.values[i].product_attribute_id == attribute.id) {
          return included.values[i];
        }
      }

      return { id: -1, value: 'Select...' };
    },
    addImagesIncluded(event, included) {
      let files = event.files;
      const formData = new FormData(); 

      for (var x = 0; x < files.length; x++) {
        // append files as array to the form, feel free to change the array name
        formData.append("images[]", files[x]);
      }

      axios
        .post(route('products.addImages', included), formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          if (!response.data.success) {
            toastr.error(response.data.message);
          } else {
            this.$inertia.visit(window.location.href, {preserveState: false, preserveScroll: true});
          }
        })
        .catch((err) => {
          console.error(err);
          toastr.error("Uploading or processing image failed.");
        });
    },
    addImagesVariation(event) {
      let files = event.files;
      const formData = new FormData(); 

      if (this.form.sku_match) {
        formData.append('sku_match', this.form.sku_match);
      }

      for (var x = 0; x < files.length; x++) {
        // append files as array to the form, feel free to change the array name
        formData.append("images[]", files[x]);
      }

      axios
        .post(route('admin.products.variationaddImages', this.product), formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          if (!response.data.success) {
            toastr.error(response.data.message);
          } else {
            this.$inertia.visit(window.location.href, {preserveState: true, preserveScroll: true});
          }
        })
        .catch((err) => {
          console.error(err);
          toastr.error("Uploading or processing image failed.");
        });
    }
  },
}
</script>

<style>
  .table th, .table td {
    padding: .02rem;
  }

  img.product-image {
    max-height: 100px;
    max-width: 100px;
  }

  .file-select {
    border: 2px dashed #0087F7;
    border-radius: 5px;
    background: white;
    min-height: 150px;
    padding: 50px;
  }

  div.p-fileupload-buttonbar {
    display: none;
  }

  .p-fileupload .p-fileupload-content {
    margin: .2rem;
    padding: 0;
  }

  .list-file-select {
    border: 2px dashed #0087F7;
    border-radius: 5px;
    background: white;
    padding: .2rem;

  }
</style>